// ============================================================
// useCommanderyAccess — member-portal state machine hook.
//
// Drives the Secure Portal UI through the approved states:
//   idle → submitting → verifying → (verified | denied | expired | revoked |
//   already_used | no_session | not_live | error)
//
// On submit it runs redeem, then verify-session, transitioning the UI to the
// verifying design immediately so the animated loader shows while both calls
// run. The one-time access code lives ONLY in ephemeral component state passed
// straight to the server proxy — never a URL, localStorage, sessionStorage, log,
// or receipt. The invite reference may be prefilled from ?invite= (id only).
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  redeemCommanderyAccess, verifyCommanderySession, getCommanderyAccessState,
  parseCommanderyInvitePrefill,
  type CommanderyAccessState, type CommanderyStateInfo,
} from './commanderyAccess'

export interface UseCommanderyAccess {
  state: CommanderyAccessState
  stateInfo: CommanderyStateInfo
  invitePrefill: string
  busy: boolean
  accountState: CommanderyAccessState | null
  submit: (input: { inviteId: string; code: string }) => Promise<void>
  reset: () => void
}

export function useCommanderyAccess(opts: { mode?: 'access' | 'session' } = {}): UseCommanderyAccess {
  const mode = opts.mode ?? 'access'
  const [state, setState] = useState<CommanderyAccessState>(mode === 'session' ? 'verifying' : 'idle')
  const [invitePrefill, setInvitePrefill] = useState('')
  const [accountState, setAccountState] = useState<CommanderyAccessState | null>(null)
  const busyRef = useRef(false)
  const [busy, setBusy] = useState(mode === 'session')

  // Prefill invite id from the URL (browser only; SSR-safe via effect). The
  // access code is never read from the URL.
  useEffect(() => {
    if (mode !== 'access') return
    if (typeof window !== 'undefined') {
      setInvitePrefill(parseCommanderyInvitePrefill(window.location.search))
    }
  }, [mode])

  // Session mode: verify an existing browser-bound session cookie on mount.
  useEffect(() => {
    if (mode !== 'session') return
    let cancelled = false
    ;(async () => {
      const v = await verifyCommanderySession({})
      if (cancelled) return
      setAccountState(v.accountState)
      setState(v.state)
      setBusy(false)
    })()
    return () => { cancelled = true }
  }, [mode])

  const submit = useCallback(async (input: { inviteId: string; code: string }) => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setAccountState(null)
    setState('submitting')
    // Show the verifying design immediately while redeem + verify run.
    setState('verifying')
    try {
      const redeem = await redeemCommanderyAccess({ inviteId: input.inviteId, code: input.code })
      if (!redeem.redeemed) {
        setState(redeem.state) // denied / expired / revoked / already_used / not_live / error
        return
      }
      const verify = await verifyCommanderySession({ inviteId: redeem.inviteId ?? input.inviteId })
      setAccountState(verify.accountState)
      setState(verify.state) // verified / no_session / ...
    } catch {
      setState('error')
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }, [])

  const reset = useCallback(() => {
    busyRef.current = false
    setBusy(false)
    setAccountState(null)
    setState('idle')
  }, [])

  return { state, stateInfo: getCommanderyAccessState(state), invitePrefill, busy, accountState, submit, reset }
}
